// Billing / plan-gate hooks (P-08) — the WRAPPER layer (lint-exempt for raw TanStack
// Query, like usePartner/useAccount). The plan is BAND-scoped, but the /activate paywall renders OUTSIDE the band
// shell (a pre-shell gate, no BandContext), so these take an explicit `bandId` rather than reading it from
// BandContext. The key is ['band', bandId, 'plan'] (the tenant in the key — the structural rule);
// staleTime:0 so the pre-paint guard and the Stripe-return refetch always see fresh subscription truth (the
// webhook is async — the plan flips shortly after checkout).
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { ApiClient, components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

export type PlanState = components["schemas"]["PlanStateResponse"];
export type RedeemResult = components["schemas"]["RedeemResponse"];
export type PlanChangeResult = components["schemas"]["PlanChangeResponse"];

/**
 * True iff the subscription grants access — the client mirror of the server's `BillingStatus.IsActive`.
 *
 * **`past_due` GRANTS ACCESS, and omitting it was a real defect** (fixed 2026-09-10). The server has always
 * treated `plan_status ∈ {active, lifetime, past_due}` as paying, and wrote down why: `past_due` is only ever
 * reached from a previously-ACTIVE subscription whose renewal invoice failed, so locking a band out during
 * the multi-day dunning window is exactly the downgrade nobody intended. This function said otherwise while
 * its own comment claimed to mirror that predicate — so a band mid-card-retry was bounced to `/activate` and
 * told its subscription was inactive, while every write it attempted still succeeded. A displayed rule
 * contradicting the enforced one, on the case the enforced one exists for.
 *
 * The Shopify rail is deliberately absent: `shopify_subscription_status` is not on this DTO, and a
 * Shopify-billed band never reaches the paywall anyway (its status arrives already resolved).
 */
export function isPlanActive(status: string | null | undefined): boolean {
  return status === "active" || status === "lifetime" || status === "past_due";
}


// ─── Waiting for the webhook ────────────────────────────────────────────────────────────────────────────
//
// An UPGRADE is applied at Stripe inside the request, but `plan_key` is written only by the webhook — the
// sole-writer rule — which lands a second or two later. `invalidateQueries` in `onSuccess` therefore refetches
// at the one moment the answer provably CANNOT have changed yet, and nothing ever looked again: the screen sat
// on the old tier under copy promising "Pro unlocks within a minute", which reads as a failed upgrade. Pressing
// it again then answers "This band is already on that plan", because by then it was. Reported exactly that way
// on 2026-09-11, on the first upgrade ever run against real Stripe.
//
// So the refetch is right and its TIMING is wrong, and the fix is to keep asking briefly.
//
// It stops on the ANSWER, not on a timer: we record the plan_key as it was, and poll until it moves. So the
// common case is one extra request a second or so later, not a fixed minute of traffic. The window is only a
// backstop for a webhook that never arrives (a bad signing secret, a missing route) — and giving up quietly is
// right there, because a poll that never ends is a tab that heats a laptop over a delivery that is not coming.
//
// ONLY the upgrade needs this. A downgrade books `pending_plan_key`, a cancel books `pending_cancel` and a
// resume clears whichever — all written by the request itself, so their refetch at T+0 reads the truth. Those
// mutations pass `applied: false`, which is exactly the discriminator.
const SETTLE_WINDOW_MS = 45_000;
const SETTLE_POLL_MS = 1_500;

// `gaveUp` rather than deleting the watch on expiry, because the two exits mean OPPOSITE things to the
// screen: plan_key moved ⇒ done, say nothing; the window ran out ⇒ the band was charged and we cannot confirm
// it, which is the one case that has to speak. Deleting on timeout would make "finished" and "failed"
// indistinguishable from the outside — the running/elapsed/no-deadline collapse, again.
type SettleWatch = { until: number; wasPlanKey: string | null; gaveUp: boolean };
const settling = new Map<string, SettleWatch>();

/** Key for a band's plan query. One definition so the mutations and the read cannot disagree about it. */
function planKeyFor(bandId: string | undefined) {
  return ["band", bandId ?? "none", "plan"] as const;
}

/** Called by a mutation that changed something only the WEBHOOK can confirm. */
function watchForWebhook(bandId: string | undefined, current: PlanState | undefined) {
  if (bandId === undefined) return;
  settling.set(bandId, { until: Date.now() + SETTLE_WINDOW_MS, wasPlanKey: current?.planKey ?? null, gaveUp: false });
}

/** Exported for tests only — a module-scoped map outlives a test file otherwise. */
export function __resetPlanSettleWatches() {
  settling.clear();
}


/**
 * Is a webhook-confirmed change still in flight, and did it give up?
 *
 * The BUTTON should stay busy for the whole operation — including the second or two the webhook takes —
 * rather than going idle and leaving a message to explain why nothing changed. That message ("Pro unlocks
 * within a minute. Refresh if it hasn't flipped") was honest when nothing polled and became wrong the moment
 * something did: it asked the reader to do what the app now does for them.
 *
 * <p>Deliberately NOT an optimistic update. Showing Pro before the server agrees is a displayed rule
 * contradicting the enforced one — the shape that produced the `past_due` paywall bug — and if the webhook
 * never lands it tells a band they bought something they do not have.</p>
 *
 * The timer exists for the give-up edge alone: every other transition re-renders anyway, because the poll
 * changes query data, but a window expiring changes nothing on its own and would otherwise leave the button
 * spinning forever.
 */
export function usePlanSettle(bandId: string | undefined): { settling: boolean; unconfirmed: boolean } {
  const [, bump] = useState(0);
  const watch = bandId === undefined ? undefined : settling.get(bandId);
  const until = watch?.until;

  useEffect(() => {
    if (until === undefined) return;
    const t = setTimeout(() => bump((n) => n + 1), Math.max(0, until - Date.now()) + 50);
    return () => clearTimeout(t);
  }, [until]);

  if (watch === undefined) return { settling: false, unconfirmed: false };
  if (watch.gaveUp || Date.now() >= watch.until) return { settling: false, unconfirmed: true };
  return { settling: true, unconfirmed: false };
}

/** `GET /bands/{bandId}/plan` — plan state for the pre-paint guard (ensureQueryData) and the /activate screen. */
export async function fetchPlan(api: ApiClient, bandId: string): Promise<PlanState> {
  const { data, error, response } = await api.GET("/api/v1/bands/{bandId}/plan", {
    params: { path: { bandId } },
  });
  if (!response.ok || !data) throw new ApiError(response.status, error);
  return data;
}

/** The band's plan state. `enabled` only with a band in hand (the paywall resolves it from /me). */
export function usePlan(bandId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: planKeyFor(bandId),
    queryFn: () => fetchPlan(api, bandId!),
    enabled: bandId !== undefined,
    staleTime: 0,
    refetchInterval: (query) => {
      const watch = bandId === undefined ? undefined : settling.get(bandId);
      if (watch === undefined) return false;

      // The webhook landed: plan_key moved. Stop on the FACT rather than waiting out the window.
      const data = query.state.data as PlanState | undefined;
      if (data !== undefined && data.planKey !== watch.wasPlanKey) {
        settling.delete(bandId!);
        return false;
      }
      // It did not arrive. Stop polling, but KEEP the watch flagged so the screen can say so.
      if (Date.now() >= watch.until) {
        watch.gaveUp = true;
        return false;
      }
      if (watch.gaveUp) return false;
      return SETTLE_POLL_MS;
    },
  });
}

/** `POST /bands/{bandId}/plan/checkout` ({planKey}) → the Stripe-hosted URL. The plan activates only on the
 * webhook (never on this redirect) — the caller sends the browser to the returned URL. May 502 if the core
 * Stripe key is unset (owner-deferred) — the error surfaces verbatim, never a phantom success.*/
export function useCheckout(bandId: string | undefined) {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (planKey: string): Promise<string> => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/plan/checkout", {
        params: { path: { bandId: bandId! } },
        body: { planKey },
      });
      if (!response.ok || !data?.checkoutUrl) throw new ApiError(response.status, error);
      return data.checkoutUrl;
    },
  });
}

/**
 * `POST /bands/{bandId}/plan/change` ({planKey}) — Upgrade to Artist CNTRL Pro, or Downgrade back to
 * standard. ONE call for both directions: the band's state at Stripe decides whether it is a checkout
 * redirect, an immediate prorated swap, or a swap scheduled for the end of the paid period, and the caller
 * renders whichever of the three fields came back.
 *
 * Invalidates the plan key so the block re-reads the new pending state. NB an UPGRADE does not flip the tier
 * here: the Stripe webhook stays the sole writer of paid plan status, so the band goes Pro seconds later —
 * which is why the block says so rather than pretending it is instant.
 */
export function useChangePlan(bandId: string | undefined) {
  const { api } = useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (planKey: string): Promise<PlanChangeResult> => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/plan/change", {
        params: { path: { bandId: bandId! } },
        body: { planKey },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: (result) => {
      // `applied` means Stripe did it inside the request — an UPGRADE swap. Its plan_key arrives by webhook,
      // so start watching. Everything else already wrote what it changed.
      if (result.applied) watchForWebhook(bandId, queryClient.getQueryData<PlanState>(planKeyFor(bandId)));
      return queryClient.invalidateQueries({ queryKey: planKeyFor(bandId) });
    },
  });
}

/**
 * `POST /bands/{bandId}/plan/cancel` — stop paying at the end of the paid period (ART-152).
 *
 * NOT `useChangePlan` with a different key: that reprices the band's one subscription, this ends it. Nothing
 * is revoked on success — the band keeps everything until the period runs out, and the webhook writes
 * `canceled` at the switchover — so the caller renders "ends on <date>", never "cancelled".
 */
export function useCancelPlan(bandId: string | undefined) {
  const { api } = useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PlanChangeResult> => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/plan/cancel", {
        params: { path: { bandId: bandId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeyFor(bandId) }),
  });
}

/** `POST /bands/{bandId}/plan/resume` — keep what you have: undo whichever pending change is booked, a
 * scheduled downgrade OR a scheduled cancellation. One call for both, because from the band's side it is one
 * decision. 409 when nothing is scheduled, surfaced verbatim rather than as a phantom success. */
export function useResumePlan(bandId: string | undefined) {
  const { api } = useApp();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<PlanChangeResult> => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/plan/resume", {
        params: { path: { bandId: bandId! } },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: planKeyFor(bandId) }),
  });
}

/** `POST /bands/{bandId}/plan/redeem` ({code}) → the redemption result. A `comp` code activates the plan
 * directly (the explicit band comp path). A bad/expired/unsupported code surfaces the server error verbatim. */
export function useRedeem(bandId: string | undefined) {
  const { api } = useApp();
  return useMutation({
    mutationFn: async (code: string): Promise<RedeemResult> => {
      const { data, error, response } = await api.POST("/api/v1/bands/{bandId}/plan/redeem", {
        params: { path: { bandId: bandId! } },
        body: { code },
      });
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
  });
}
